import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Page, Table, Textarea, useToast } from "@theg1group/ui";
import type { TableColumn } from "@theg1group/ui";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api.ts";
import { API_BASE } from "../config.ts";

export function CaseView() {
	const { id } = useParams();
	return id ? <CaseDetail id={id} /> : <CaseList />;
}

interface CaseRow {
	id: string;
	name: string;
	status: string;
	created_at: string;
	from_domain: string | null;
	item_count: number;
	classification: string | null;
	received: string | null;
}

function CaseList() {
	const { data, isLoading } = useQuery({
		queryKey: ["cases"],
		queryFn: () => api.get<{ rows: CaseRow[] }>("/api/cases"),
	});
	const columns: TableColumn<CaseRow>[] = [
		{
			id: "name",
			header: "Case",
			accessor: (r) => (
				<Link to={`/cases/${r.id}`} className="text-primary hover:underline">
					{r.name}
				</Link>
			),
		},
		{ id: "from", header: "From domain", accessor: (r) => r.from_domain ?? "—" },
		{ id: "class", header: "Classification", accessor: (r) => r.classification ?? "—" },
		{ id: "items", header: "Items", accessor: (r) => r.item_count },
		{ id: "status", header: "Status", accessor: (r) => <Badge>{r.status}</Badge> },
		{
			id: "received",
			header: "Received",
			accessor: (r) => (r.received ? new Date(r.received).toLocaleString() : "—"),
		},
		{ id: "created", header: "Added", accessor: (r) => new Date(r.created_at).toLocaleDateString() },
	];
	return (
		<Page title="Cases" description="Each uploaded email is a case.">
			<Card>
				<div className="overflow-x-auto">
					<Table columns={columns} data={data?.rows ?? []} keyExtractor={(r) => r.id} loading={isLoading} emptyMessage="No cases yet." />
				</div>
			</Card>
		</Page>
	);
}

interface CaseDetailData {
	case: { id: string; name: string; status: string; notes: string | null };
	items: Array<{ item_type: string; item_id: string; label: string | null; added_at: string }>;
	findings: Array<{ id: string; kind: string; severity: string | null; summary: string }>;
}

function CaseDetail({ id }: { id: string }) {
	const qc = useQueryClient();
	const toast = useToast();
	const [notes, setNotes] = useState<string | null>(null);
	const { data } = useQuery({
		queryKey: ["case", id],
		queryFn: () => api.get<CaseDetailData>(`/api/cases/${id}`),
	});
	const save = useMutation({
		mutationFn: () => api.patch(`/api/cases/${id}`, { notes: notes ?? data?.case.notes ?? "" }),
		onSuccess: () => {
			toast.addToast("success", "Saved");
			qc.invalidateQueries({ queryKey: ["case", id] });
		},
	});

	async function downloadIoc(format: "json" | "csv") {
		const res = await fetch(`${API_BASE}/api/cases/${id}/ioc?format=${format}`, {
			credentials: "include",
		});
		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `ioc-${id}.${format}`;
		a.click();
		URL.revokeObjectURL(url);
	}

	if (!data) return <Page title="Case">Loading…</Page>;
	const notesVal = notes ?? data.case.notes ?? "";

	return (
		<Page
			title={data.case.name}
			description={`case ${id}`}
			actions={
				<div className="flex gap-2">
					<Link to={`/messages/${id}`} className="text-primary text-sm hover:underline self-center">
						Open message →
					</Link>
					<Button variant="outline" onClick={() => downloadIoc("csv")}>
						Export IOC (CSV)
					</Button>
					<Button variant="outline" onClick={() => downloadIoc("json")}>
						JSON
					</Button>
				</div>
			}
		>
			<div className="grid gap-3.5" style={{ gridTemplateColumns: "2fr 1fr" }}>
				<div>
					<Card className="mb-3.5">
						<CardBody>
							<h2 className="text-sm font-semibold text-content mb-2">Findings</h2>
							{data.findings.map((f) => (
								<div key={f.id} className="text-sm mb-1">
									<Badge variant="warning">{f.kind}</Badge> {f.summary}
								</div>
							))}
							{!data.findings.length && <div className="text-xs text-muted">—</div>}
						</CardBody>
					</Card>
					<Card>
						<CardBody>
							<h2 className="text-sm font-semibold text-content mb-2">Notes</h2>
							<Textarea
								value={notesVal}
								onChange={(e) => setNotes(e.target.value)}
								className="w-full min-h-[140px] text-sm"
							/>
							<div className="mt-2">
								<Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
									Save notes
								</Button>
							</div>
						</CardBody>
					</Card>
				</div>
				<Card>
					<CardBody>
						<h2 className="text-sm font-semibold text-content mb-2">Items ({data.items.length})</h2>
						{data.items.map((it) => (
							<div key={`${it.item_type}:${it.item_id}`} className="text-sm mb-1">
								<Badge>{it.item_type}</Badge> <span className="text-content break-all">{it.label ?? it.item_id}</span>
							</div>
						))}
					</CardBody>
				</Card>
			</div>
		</Page>
	);
}
